import { switchClasses, tooltipClasses } from '@private/components';
import {
  ColumnsSettingsButton,
  dataGridClasses,
  gridSettingsButtonClasses as classes,
} from '@private/data-grid';
import { getBorderBase, getFontBase, getPrefixedVar } from '@private/payment';
import styled, { css } from 'styled-components';

import Button from 'components/Button';
import { getInputTheme } from 'components/Controls/Input/styled';
import Switch from 'components/Controls/Switch';
import { getSwitchTheme } from 'components/Controls/Switch/styled';
import { getScrollbarTheme } from 'components/Scrollbar';

const prefix = ['components', 'dataGrid', 'columnsFilter', 'popover'];

export const ColumnsSettings = styled(ColumnsSettingsButton)`
  ${tooltipClasses.message} {
    ${getBorderBase([...prefix])};

    background: ${getPrefixedVar(prefix, 'bg')};
    box-shadow: 0 4px 22px ${getPrefixedVar(prefix, 'boxShadow')};
  }

  ${classes.settingsColumn} {
    width: 33.3333%;
    flex-grow: 1;
    min-width: 33.3333%;

    &:not(:last-child) {
      border-right-style: solid;
      ${getBorderBase([...prefix, 'settingsColumn'])};
    }

    &:nth-child(2) {
      position: relative;
      padding-left: 8px;

      &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 30px;
        background: linear-gradient(
          180deg,
          transparent 0%,
          ${getPrefixedVar(prefix, 'settingsColumn', 'shadowColor')} 100%
        );
        pointer-events: none;
      }
    }
  }

  ${classes.columnsSettingsTitle} {
    ${getFontBase([...prefix, 'title'])};
  }

  ${classes.settingsColumnTitle}, ${classes.columnTitleText} {
    ${getFontBase([...prefix, 'columnsTitle'])};

    text-transform: uppercase;
  }

  ${classes.settingsColumnBody} {
    padding: 2px;
    ${getScrollbarTheme([...prefix, 'scrollbar'])}
  }

  ${classes.pinDropZone}, ${classes.emptyResultContainer} {
    ${getBorderBase([...prefix, 'settingsColumn', 'pinDropZone', 'base'])};

    background: ${getPrefixedVar(prefix, 'settingsColumn', 'pinDropZone', 'base', 'bg')};

    &[data-is-over='true'] {
      border-style: dashed;
      ${getBorderBase([...prefix, 'settingsColumn', 'pinDropZone', 'over'])};

      background: ${getPrefixedVar(
        prefix,
        'settingsColumn',
        'pinDropZone',
        'over',
        'bg',
      )};
    }
  }

  ${classes.pinColumnText} {
    max-width: 190px;
    ${getFontBase([...prefix, 'settingsColumn', 'pinDropZone', 'pinText'])};
  }

  ${classes.columnSettingItem} {
    min-height: 40px;
    ${getBorderBase([...prefix, 'settingItem'])};

    background: ${getPrefixedVar(prefix, 'settingItem', 'bg')};

    @media (width <= 1000px) {
      min-height: 48px;
    }
  }

  ${classes.settingDraggablePart} {
    background: ${getPrefixedVar(prefix, 'settingItem', 'dragController', 'bg')};
    color: ${getPrefixedVar(prefix, 'settingItem', 'dragController', 'icon')};
    ${getBorderBase([...prefix, 'settingItem', 'dragController'])};

    @media (width <= 1000px) {
      flex: 0 0 40px;
    }
  }

  ${classes.settingFixedDraggablePart} {
    background: ${getPrefixedVar(
      prefix,
      'settingItem',
      'fixedDragController',
      'base',
      'bg',
    )};
    transition: background 0.2s ease-in-out;

    &:hover {
      background: ${getPrefixedVar(
        prefix,
        'settingItem',
        'fixedDragController',
        'hover',
        'bg',
      )};
    }
  }

  ${classes.columnSettingSwitch} {
    ${getSwitchTheme()}
  }

  ${classes.settingContent} {
    width: calc(100% - 40px);
    padding-left: 10px;
    padding-right: 10px;
    column-gap: 4px;
  }

  ${classes.settingName} {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    ${getFontBase([...prefix, 'settingItem'])};
  }

  ${classes.settingDropZoneBody} {
    border-style: dashed;
    ${getBorderBase([...prefix, 'settingItem', 'dragArea', 'base'])};

    &[data-is-over='true'] {
      height: 40px;
      ${getBorderBase([...prefix, 'settingItem', 'dragArea', 'active'])};

      background: ${getPrefixedVar([
        ...prefix,
        'settingItem',
        'dragArea',
        'active',
        'bg',
      ])};
    }
  }

  ${classes.searchInput} {
    ${getInputTheme() as never};
  }

  ${dataGridClasses.settingsExpansionSwitcher} {
    color: ${getPrefixedVar(prefix, 'settingsColumn', 'stretchButton', 'base', 'text')};

    &:hover {
      color: ${getPrefixedVar(
        prefix,
        'settingsColumn',
        'stretchButton',
        'hover',
        'text',
      )};
    }
  }

  ${dataGridClasses.columnStretchSwitcher} {
    position: relative;
    z-index: 2;
    padding-left: 4px;
    padding-bottom: 2px;
    color: ${getPrefixedVar(prefix, 'settingsColumn', 'stretchButton', 'base', 'text')};

    &:hover {
      color: ${getPrefixedVar(
        prefix,
        'settingsColumn',
        'stretchButton',
        'hover',
        'text',
      )};
    }
  }
  ${classes.columnsTogglers} {
    gap: 32px;
  }

  ${classes.visibilityButtonsWrapper} {
    position: relative;
    background: ${getPrefixedVar(prefix, 'visibilityHandler', 'bg')};
    ${getBorderBase([...prefix, 'visibilityHandler'])};

    &:before {
      content: '';
      position: absolute;
      height: 32px;
      width: 1px;
      left: -16px;
      top: 0;
      background: ${getPrefixedVar(prefix, 'visibilityHandler', 'divider')};
    }
  }

  ${classes.visibilityButton} {
    background: ${getPrefixedVar(prefix, 'visibilityHandler', 'button', 'base', 'bg')};
    ${getFontBase([...prefix, 'visibilityHandler', 'button', 'base'])};

    &:hover {
      background: ${getPrefixedVar(prefix, 'visibilityHandler', 'button', 'hover', 'bg')};
      ${getFontBase([...prefix, 'visibilityHandler', 'button', 'hover'])};
    }

    &[data-is-selected='true'] {
      background: ${getPrefixedVar(
        prefix,
        'visibilityHandler',
        'button',
        'selected',
        'bg',
      )};
      ${getFontBase([...prefix, 'visibilityHandler', 'button', 'selected'])};
    }
  }
` as typeof ColumnsSettingsButton;

export const SettingsButton = styled(Button)<{ $isOpen: boolean }>`
  ${({ $isOpen }) =>
    $isOpen &&
    css`
      color: ${getPrefixedVar([
        'components',
        'dataGrid',
        'columnsFilter',
        'activeSettingButton',
        'text',
      ])};
      background: ${getPrefixedVar([
        'components',
        'dataGrid',
        'columnsFilter',
        'activeSettingButton',
        'bg',
      ])};
    `}
`;

export const StyledSwitch = styled(Switch)`
  flex-direction: row-reverse;

  &,
  &[data-ischecked='true'],
  &[data-ischecked='false'],
  &:hover,
  &:active,
  &[data-ischecked='true']:active,
  &:hover:not(:active, [data-isdisabled='true']),
  &:hover:not(:active, [data-isdisabled='true'])[data-ischecked='true'] {
    ${switchClasses.label} {
      ${getFontBase([...prefix, 'enableAllSwitch', 'label'])}
    }
  }
`;

export const FiltersFallbackWrapper = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 10px;
`;
